import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CompanyCard } from "@/components/companies/CompanyCard";
import { CompanyDetail } from "@/components/companies/CompanyDetail";
import { DatePickerWithRange } from "@/components/ui/date-range-picker";
import { LoadingState } from "@/components/ui/loading-state";
import { DateRange } from "react-day-picker";
import { ContactProfile } from "@/components/contacts/ContactProfile";
import { CreateCompanyModal } from "@/components/companies/CreateCompanyModal";
import { EditCompanyModal } from "@/components/companies/EditCompanyModal";
import {
  companyAPI,
  Company,
  CreateCompanyData,
  UpdateCompanyData,
} from "@/services/companyService";
import { contactoAPI } from "@/services/contactoService";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Plus,
  Search,
  Building2,
  Factory,
  Globe,
  Calendar,
  Users,
  ChevronLeft,
  ChevronRight,
  X,
} from "lucide-react";
import { useDebounce } from "@/hooks/useDebounce";
import { IndustriesManagement } from "@/components/industries/IndustriesManagement";

export default function Companies() {
  const { toast } = useToast();
  const { company, isAuthenticated } = useAuth();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [selectedContact, setSelectedContact] = useState<any | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFilter, setDateFilter] = useState<DateRange | undefined>(undefined);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [isIndustriesModalOpen, setIsIndustriesModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const getCompanySlug = (): string | null => {
    if (company?.slug) {
      return company.slug;
    }

    try {
      const storedCompany = localStorage.getItem("company");
      if (storedCompany) {
        const parsedCompany = JSON.parse(storedCompany);
        if (parsedCompany?.slug) {
          return parsedCompany.slug;
        }
      }
    } catch (error) {}

    try {
      const sessionCompany = sessionStorage.getItem("company");
      if (sessionCompany) {
        const parsedCompany = JSON.parse(sessionCompany);
        if (parsedCompany?.slug) {
          return parsedCompany.slug;
        }
      }
    } catch (error) {}

    try {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        if (parsedUser?.company?.slug) {
          return parsedUser.company.slug;
        }
      }
    } catch (error) {}

    return null;
  };

  const companySlug = getCompanySlug();

  useEffect(() => {
    if (isAuthenticated && companySlug) {
      fetchCompanies(currentPage);
    } else if (!isAuthenticated) {
      setLoading(false);
      toast({
        title: "Acceso requerido",
        description: "Debes iniciar sesión para ver las empresas",
        variant: "destructive",
      });
    } else if (!companySlug) {
      setLoading(false);
      toast({
        title: "Error de configuración",
        description: "No se pudo identificar la organización",
        variant: "destructive",
      });
    }
  }, [isAuthenticated, companySlug]);

  const fetchCompanies = async (pageToFetch = 1) => {
    try {
      setLoading(true);
      const response: any = await companyAPI.getCompanies({
        slug: companySlug!,
        limit: 1000,
      });

      const companiesList = Array.isArray(response)
        ? response
        : Array.isArray(response.data)
          ? response.data
          : [];

      setCompanies(companiesList);

      if (companiesList.length === 0) {
        toast({
          title: "Sin empresas",
          description: "No hay empresas registradas. Crea la primera.",
        });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Error al cargar empresas",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getIndustryName = (company: Company): string => {
    if (!company) return "";

    if (typeof company.industry === "string") {
      return company.industry;
    }

    if (company.industry && typeof company.industry === "object") {
      const industry = company.industry as any;
      return industry.name || company.industry_id || "";
    }

    return company.industry_id || "";
  };

  const totalCompanies = companies.length;

  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();
  const currentMonthName = new Date().toLocaleString("es-ES", {
    month: "long",
  });
  const newCompaniesThisMonth = companies.filter((c) => {
    const createdAt = (c as any).createdAt || (c as any).created_at;
    if (!createdAt) return false;
    const date = new Date(createdAt);
    return (
      date.getMonth() === currentMonth && date.getFullYear() === currentYear
    );
  }).length;

  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const filteredCompanies = useMemo(
    () =>
      companies.filter((company) => {
        const matchesSearch =
          company.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          getIndustryName(company).toLowerCase().includes(searchQuery.toLowerCase());
          
        let matchesDate = true;
        const createdAt = (company as any).createdAt || (company as any).created_at;
        if (dateFilter?.from && createdAt) {
          const date = new Date(createdAt);
          // Set hours to 0 to compare days properly
          date.setHours(0, 0, 0, 0);
          
          const from = new Date(dateFilter.from);
          from.setHours(0, 0, 0, 0);
          
          if (dateFilter.to) {
            const to = new Date(dateFilter.to);
            to.setHours(23, 59, 59, 999);
            matchesDate = date >= from && date <= to;
          } else {
            matchesDate = date.getTime() === from.getTime();
          }
        }
        return matchesSearch && matchesDate;
      }),
    [companies, searchQuery, dateFilter]
  );

  const ITEMS_PER_PAGE = 9;
  const currentTotalPages = Math.ceil(filteredCompanies.length / ITEMS_PER_PAGE) || 1;
  const paginatedCompanies = filteredCompanies.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, dateFilter]);

  const hasFilters = searchQuery.length > 0 || dateFilter?.from;

  const clearFilters = () => {
    setSearchQuery("");
    setDateFilter(undefined);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= currentTotalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleCompanyClick = async (company: Company) => {
    // Open immediately with the list data so modal appears fast
    setSelectedCompany(company);
    // Then fetch fresh data with signed URLs (rtu_url, logo_url)
    if (companySlug) {
      try {
        setDetailLoading(true);
        const freshResponse = await companyAPI.getCompanyById(company.id, companySlug);
        if (freshResponse.success) {
          setSelectedCompany(freshResponse.data);
        }
      } catch (e) {
        // Keep original data if fetch fails
      } finally {
        setDetailLoading(false);
      }
    }
  };

  const handleContactClick = (contact: any) => {
    setSelectedContact(contact);
    setSelectedCompany(null);
  };

  const handleSaveCompany = async (companyData: CreateCompanyData, contactData?: any, fileToUpload?: File) => {
    if (!companySlug) {
      toast({
        title: "Error",
        description: "No se encontró el slug de la organización",
        variant: "destructive",
      });
      return;
    }

    try {
      setSaveLoading(true);
      const response = await companyAPI.createCompany({
        ...companyData,
        slug: companySlug,
      });

      const companyId = response.data?.id;

      if (contactData && contactData.firstName) {
        try {
          const newContact = {
            slug: companySlug,
            name: `${contactData.firstName} ${contactData.lastName}`.trim(),
            firstName: contactData.firstName,
            lastName: contactData.lastName,
            email: contactData.email || "",
            phone: contactData.phone ? `${contactData.countryCode || "+503"} ${contactData.phone}` : "",
            position: contactData.position || "",
            client_company_id: companyId,
          };
          await contactoAPI.createContacto(companySlug, newContact);
        } catch (contactError) {
          console.error("Error al crear el contacto principal:", contactError);
          toast({
            title: "Advertencia",
            description: "La empresa se creó, pero hubo un error al crear el contacto principal.",
            variant: "destructive",
          });
        }
      }

      if (companyId && fileToUpload) {
        try {
          await companyAPI.uploadAttachment(companyId, companySlug, fileToUpload);
          toast({ title: "Adjunto subido", description: "El archivo RTU se subió exitosamente." });
        } catch (err: any) {
          console.error("Error al subir adjunto de empresa:", err);
          toast({
            title: "Error de adjunto",
            description: err.message || "Falló la subida del adjunto.",
            variant: "destructive"
          });
        }
      }

      let finalCompany = response.data;
      if (companyId) {
        try {
          const freshResponse = await companyAPI.getCompanyById(companyId, companySlug);
          if (freshResponse.success) {
            finalCompany = freshResponse.data;
          }
        } catch (freshError) {
          console.error("Error fetching fresh company details:", freshError);
        }
      }

      setCompanies((prev) => [...prev, finalCompany]);
      setIsCreateModalOpen(false);

      toast({
        title: "Empresa creada",
        description: `La empresa "${companyData.name}" ha sido creada exitosamente.`,
      });
      await fetchCompanies();
      return companyId;
    } catch (error: any) {
      console.error("Error al crear empresa:", error);

      toast({
        title: "Error",
        description: error.message || "Error al crear empresa",
        variant: "destructive",
      });

      if (error.message && error.message.includes("creó")) {
        console.log(
          "Recargando lista de empresas por si se creó correctamente...",
        );
        await fetchCompanies();
        setIsCreateModalOpen(false);
      }
    } finally {
      setSaveLoading(false);
    }
  };

  const handleUpdateCompany = async (id: string, data: UpdateCompanyData, contactData?: any, fileToUpload?: File) => {
    if (!companySlug) {
      toast({
        title: "Error",
        description: "No se encontró el slug de la organización",
        variant: "destructive",
      });
      return;
    }

    try {
      const updateData = {
        ...data,
        slug: companySlug,
      };
      const response = await companyAPI.updateCompany(id, updateData);
      let finalCompany = response.data;

      if (contactData && contactData.firstName) {
        try {
          const contactUpdatePayload = {
            name: `${contactData.firstName} ${contactData.lastName}`.trim(),
            firstName: contactData.firstName,
            lastName: contactData.lastName,
            email: contactData.email || "",
            phone: contactData.phone ? `${contactData.countryCode || "+503"} ${contactData.phone}` : "",
            position: contactData.position || "",
          };
          if (contactData.id) {
            await contactoAPI.updateContacto(companySlug, contactData.id, contactUpdatePayload);
          } else {
            await contactoAPI.createContacto(companySlug, {
              ...contactUpdatePayload,
              slug: companySlug,
              client_company_id: id,
            });
          }
        } catch (contactError) {
          console.error("Error al actualizar contacto principal:", contactError);
        }
      }

      if (fileToUpload) {
        try {
          await companyAPI.uploadAttachment(id, companySlug, fileToUpload);
          toast({ title: "Adjunto subido", description: "El archivo RTU se subió exitosamente." });
        } catch (err: any) {
          console.error("Error al subir adjunto de empresa:", err);
          toast({
            title: "Error de adjunto",
            description: err.message || "Falló la subida del adjunto.",
            variant: "destructive"
          });
        }
      }

      try {
        const freshResponse = await companyAPI.getCompanyById(id, companySlug);
        if (freshResponse.success) {
          finalCompany = freshResponse.data;
        }
      } catch (freshError) {
        console.error("Error fetching fresh company details:", freshError);
      }

      setCompanies(
        companies.map((company) =>
          company.id === id ? finalCompany : company,
        ),
      );

      if (selectedCompany?.id === id) {
        setSelectedCompany(finalCompany);
      }

      setIsEditModalOpen(false);
      setEditingCompany(null);

      toast({
        title: "Empresa actualizada",
        description: "Empresa actualizada correctamente",
      });
      await fetchCompanies();
    } catch (error: any) {
      toast({
        title: "❌ Error",
        description: error.message || "Error al actualizar empresa",
        variant: "destructive",
      });
    }
  };

  const handleEditClick = (id: string, companyData: Partial<Company>) => {
    if (!selectedCompany) return;

    setEditingCompany(selectedCompany);
    setIsEditModalOpen(true);
  };

  const handleEditFromCard = (company: Company, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingCompany(company);
    setIsEditModalOpen(true);
  };

  const handleDeleteFromCard = (companyId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setCompanyToDelete(companyId);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteCompany = async () => {
    if (!companyToDelete) return;
    await handleDeleteCompany(companyToDelete);
    setDeleteDialogOpen(false);
    setCompanyToDelete(null);
  };

  const handleDeleteCompany = async (id: string) => {
    if (!companySlug) return;

    try {
      await companyAPI.deleteCompany(id, companySlug);
      setCompanies(companies.filter((company) => company.id !== id));
      setSelectedCompany(null);
      toast({
        title: "Empresa eliminada",
        description: "Empresa eliminada correctamente",
      });
    } catch (error: any) {
      toast({
        title: "❌ Error",
        description: error.message || "Error al eliminar empresa",
        variant: "destructive",
      });
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">
            Acceso no autorizado
          </h1>
          <p className="text-slate-600">
            Debes iniciar sesión para ver esta página
          </p>
        </div>
      </div>
    );
  }

  if (!companySlug) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">
            Error de configuración
          </h1>
          <p className="text-slate-600 mb-4">
            No se pudo identificar tu organización. Por favor, contacta al
            administrador.
          </p>
          <Button onClick={() => window.location.reload()}>Reintentar</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="bg-blue-100 p-2.5 rounded-2xl">
              <Building2 className="h-7 w-7 text-blue-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Empresas</h1>
          </div>
          <p className="text-slate-500 text-sm md:text-base ml-1">Gestiona todas tus empresas cliente</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          <Button
            variant="outline"
            onClick={() => setIsIndustriesModalOpen(true)}
            className="w-full sm:w-auto justify-center rounded-xl border-slate-200 hover:bg-slate-50 text-slate-700 font-medium transition-all"
          >
            <Factory className="h-4 w-4 mr-2 text-slate-500" />
            Industrias
          </Button>
          <Button
            onClick={() => setIsCreateModalOpen(true)}
            className="bg-slate-900 hover:bg-black text-white w-full sm:w-auto justify-center rounded-xl shadow-lg shadow-slate-900/20 font-medium transition-all"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nueva Empresa
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-slate-50/80 border border-slate-200/80 rounded-[20px] p-4 shadow-sm hover:shadow-md transition-all flex items-center justify-between group">
          <div>
            <p className="text-xs font-bold text-slate-500 mb-1">Total Empresas</p>
            <p className="text-2xl font-black text-slate-900 tracking-tight">
              {totalCompanies}
            </p>
          </div>
          <div className="h-10 w-10 bg-blue-100/50 text-blue-600 shadow-sm rounded-full flex items-center justify-center group-hover:scale-110 transition-transform ring-1 ring-blue-100">
            <Building2 className="h-5 w-5" />
          </div>
        </div>

        <div className="bg-slate-50/80 border border-slate-200/80 rounded-[20px] p-4 shadow-sm hover:shadow-md transition-all flex items-center justify-between group">
          <div>
            <p className="text-xs font-bold text-slate-500 mb-1">
              Nuevas este Mes{" "}
              <span className="capitalize text-slate-400 font-medium">
                ({currentMonthName})
              </span>
            </p>
            <p className="text-2xl font-black text-orange-500 tracking-tight">
              {newCompaniesThisMonth}
            </p>
          </div>
          <div className="h-10 w-10 bg-orange-100/50 text-orange-500 shadow-sm rounded-full flex items-center justify-center group-hover:scale-110 transition-transform ring-1 ring-orange-100">
            <Calendar className="h-5 w-5" />
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Buscar empresas por nombre o industria..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto">
          <div className="w-full sm:w-auto min-w-[260px]">
            <DatePickerWithRange date={dateFilter} setDate={setDateFilter} />
          </div>
          {hasFilters && (
            <Button
              variant="ghost"
              onClick={clearFilters}
              className="text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-full px-4"
            >
              <X className="h-4 w-4 mr-2" />
              Limpiar
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <LoadingState type="grid" message="Cargando empresas..." />
      ) : filteredCompanies.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-lg">
          <div className="mx-auto w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
            <Building2 className="h-8 w-8 text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">
            No hay empresas registradas
          </h3>
          <p className="text-slate-600 mb-6">
            Comienza creando tu primera empresa cliente
          </p>
          <Button
            onClick={() => setIsCreateModalOpen(true)}
            className="bg-slate-900 hover:bg-black"
          >
            <Plus className="h-4 w-4 mr-2" />
            Crear primera empresa
          </Button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {paginatedCompanies.map((company) => (
              <CompanyCard
                key={company.id}
                company={company}
                onClick={handleCompanyClick}
                onEdit={handleEditFromCard}
                onDelete={handleDeleteFromCard}
              />
            ))}
          </div>

          {filteredCompanies.length > 0 && (
            <div className="flex flex-col items-center gap-6 mt-8">
              <div className="flex items-center justify-center gap-2">
                <Button 
                  variant="outline" 
                  size="icon" 
                  className="rounded-full h-10 w-10 border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-blue-600 transition-colors"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: currentTotalPages }).map((_, i) => (
                    <button
                      key={i}
                      onClick={() => handlePageChange(i + 1)}
                      className={`h-10 w-10 rounded-full text-sm font-medium transition-all ${
                        currentPage === i + 1 
                          ? "bg-slate-900 text-white shadow-md shadow-slate-900/30" 
                          : "text-slate-600 hover:bg-slate-100 hover:text-blue-600"
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
                <Button 
                  variant="outline" 
                  size="icon" 
                  className="rounded-full h-10 w-10 border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-blue-600 transition-colors"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === currentTotalPages}
                >
                  <ChevronRight className="h-5 w-5" />
                </Button>
              </div>
              <div className="text-sm font-medium text-slate-500 bg-slate-50 px-4 py-2 rounded-full border border-slate-100">
                Mostrando <span className="text-slate-900">{paginatedCompanies.length}</span> de <span className="text-slate-900">{filteredCompanies.length}</span> empresas
              </div>
            </div>
          )}
        </>
      )}

      <CompanyDetail
        company={selectedCompany}
        isOpen={!!selectedCompany}
        onClose={() => setSelectedCompany(null)}
        onContactClick={handleContactClick}
        onUpdate={handleEditClick}
        onDelete={handleDeleteCompany}
        detailLoading={detailLoading}
      />

      <ContactProfile
        contact={selectedContact}
        isOpen={!!selectedContact}
        onClose={() => setSelectedContact(null)}
      />

      <CreateCompanyModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSave={handleSaveCompany}
        companySlug={companySlug!}
      />

      <EditCompanyModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingCompany(null);
        }}
        onSave={handleUpdateCompany}
        company={editingCompany}
        companySlug={companySlug!}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Esto eliminará permanentemente
              la empresa y todos sus datos asociados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteCompany}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={isIndustriesModalOpen}
        onOpenChange={setIsIndustriesModalOpen}
      >
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto custom-scrollbar p-6 rounded-[24px]">
          <DialogHeader className="mb-4">
            <DialogTitle className="text-2xl font-bold text-slate-900">Gestión de Industrias</DialogTitle>
          </DialogHeader>
          <IndustriesManagement />
        </DialogContent>
      </Dialog>
    </div>
  );
}
